import { wall, type Courier, type Stop, REASONS, type Reason } from '@day';
import { ZONE_COUNTRY } from './zone_countries';

/**
 * Words and numbers as the design page writes them (control-room-web.html, "Words and numbers"), on the business's
 * clock: every time is the business's own (`where.timeZone`), whatever the computer's zone.
 */

/**
 * How the business's country writes dates and times: the day before the month or after it, and a 12- or 24-hour clock,
 * in English. From CLDR's data through Intl (English's own for that country where there is one, else the country's main
 * language), except Nigeria: it writes "3:05 PM" day to day, as the app always has, though CLDR says 24-hour.
 */
export interface DateStyle {
  dayFirst: boolean;
  h12: boolean;
}

const NIGERIA: DateStyle = { dayFirst: true, h12: true };
let style: DateStyle = NIGERIA;

export function dateStyleOf(country: string | null | undefined): DateStyle {
  const c = country?.toUpperCase() ?? '';
  if (!/^[A-Z]{2}$/.test(c) || c === 'NG') return NIGERIA;
  try {
    const en = `en-${c}`;
    const base = new Intl.DateTimeFormat(en).resolvedOptions().locale === en ? en : `${new Intl.Locale(`und-${c}`).maximize().language}-${c}`;
    const parts = new Intl.DateTimeFormat(base, { day: 'numeric', month: 'long', timeZone: 'UTC' }).formatToParts(Date.UTC(2026, 9, 31));
    const dayFirst = parts.findIndex((p) => p.type === 'day') < parts.findIndex((p) => p.type === 'month');
    const region = new Intl.Locale(`und-${c}`) as Intl.Locale & { getHourCycles?: () => string[]; hourCycles?: string[] };
    const cycle = region.getHourCycles?.()[0] ?? region.hourCycles?.[0] ?? new Intl.DateTimeFormat(base, { hour: 'numeric' }).resolvedOptions().hourCycle;
    return { dayFirst, h12: cycle === 'h12' || cycle === 'h11' };
  } catch {
    return NIGERIA;
  }
}

/** The style the pages write in, set as the model is built (the business's confirmed country, else this computer's). */
export const useDateStyle = (s: DateStyle) => void (style = s);

/**
 * A computer's country from its clock's time zone first ("Africa/Lagos" → NG, the tz database's own table, generated into
 * zone_countries.ts), then from its language ("en-GB" → GB): an en-US browser in Lagos is in Nigeria. Only offered: the
 * owner confirms where the business works.
 */
export function countryOf(zone: string | null | undefined, language: string | null | undefined): string | null {
  const byZone = zone ? ZONE_COUNTRY[zone] : undefined;
  if (byZone) return byZone;
  try {
    return language ? (new Intl.Locale(language).maximize().region ?? null) : null;
  } catch {
    return null;
  }
}

/** This computer's country (its time zone, else its language), for a business that hasn't confirmed where it works. */
export function computerCountry(): string | null {
  let zone: string | null = null;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    zone = null;
  }
  return countryOf(zone, typeof navigator === 'undefined' ? null : navigator.language);
}

/** "12:40 PM", or "12:40" where the clock is 24-hour ("09:05" in the morning). */
export function time(t: number | null | undefined, zone: string, s: DateStyle = style): string {
  if (t == null) return '–';
  const w = wall(t, zone);
  const mm = String(w.minute).padStart(2, '0');
  return s.h12 ? `${((w.hour + 11) % 12) + 1}:${mm} ${w.hour < 12 ? 'AM' : 'PM'}` : `${String(w.hour).padStart(2, '0')}:${mm}`;
}

/** An hour on a chart's axis: "9 AM", or "09:00" where the clock is 24-hour. */
export const hourWords = (h: number, s: DateStyle = style) => (s.h12 ? `${((h + 11) % 12) + 1} ${h < 12 ? 'AM' : 'PM'}` : `${String(h).padStart(2, '0')}:00`);

/** The day a moment falls on in a zone ("2026-10-31"). */
export const dayOf = (t: number, zone: string) => new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(t));

/** "just now", "12 min ago", "3 h ago". */
export function ago(t: number, now: number): string {
  const d = Math.round((now - t) / 60000);
  return d < 1 ? 'just now' : d < 60 ? `${d} min ago` : `${Math.floor(d / 60)} h ago`;
}

/** "12 min", "1 h 5 min". */
export function inMinutes(msLeft: number): string {
  const r = Math.max(1, Math.round(msLeft / 60000));
  return r < 60 ? `${r} min` : `${Math.floor(r / 60)} h ${r % 60} min`;
}

/**
 * How a stop stands against its deliver-by time, for the Stops table: in a word, with the times for the hover, and [rank]
 * to sort by, the worst first. Null: nothing to measure (no deliver-by time, or no time yet). A deliver-by time has no
 * start, so Early means at least 30 minutes ahead of it. [ifNow]: when a route not started would get there if it left now.
 */
export function timingOf(s: Pick<Stop, 'state' | 'at' | 'eta' | 'deliverBy'>, now: number, zone: string, ifNow?: number | null): { word: string; tone: '' | 'pos' | 'neg' | 'cau'; tip: string; rank: number } | null {
  const by = s.deliverBy;
  const byWords = by != null ? ` · deliver by ${time(by, zone)}` : '';
  if (s.state === 'failed') return { word: 'Not done', tone: '', tip: `Not delivered ${time(s.at, zone)}${byWords}`, rank: 2 };
  if (s.state === 'left') return { word: 'Not done', tone: '', tip: `The route ended before this stop${byWords}`, rank: 2 };
  if (by == null) return null;
  if (s.state === 'done') {
    if (s.at == null) return null;
    const tip = `Delivered ${time(s.at, zone)}${byWords}`;
    if (s.at > by) return { word: 'Late', tone: 'neg', tip, rank: 0 };
    return s.at <= by - 30 * 60000 ? { word: 'Early', tone: 'pos', tip, rank: 4 } : { word: 'On time', tone: 'pos', tip, rank: 3 };
  }
  const expected = s.eta ?? ifNow ?? null;
  const tip = `${expected != null ? `About ${time(expected, zone)}${s.eta == null ? ' if the route leaves now' : ''}` : 'Not delivered yet'}${byWords}`;
  if (now > by) return { word: 'Late', tone: 'neg', tip, rank: 0 };
  if (expected == null) return null;
  return expected > by ? { word: 'At risk', tone: 'cau', tip, rank: 1 } : { word: 'On time', tone: 'pos', tip, rank: 3 };
}

/** A quiet spell, as the phone says it (`_quietFor`). */
export function quietFor(msQuiet: number): string {
  const m = Math.floor(msQuiet / 60000);
  return m < 120 ? `${m} min` : `${Math.floor(m / 60)} hours`;
}

const SYMBOLS: Record<string, string> = { NGN: '₦', GHS: 'GH₵', KES: 'KSh ', GBP: '£', USD: '$', EUR: '€', ZAR: 'R' };

/** "₦8,500" from minor units (kobo), and "₦4,537.50" when there are kobo, as the phone's Money.format shows them. */
export function money(minor: number, currency = 'NGN'): string {
  const sign = minor < 0 ? '−' : '';
  const n = Math.abs(Math.round(minor));
  const digits = n % 100 ? 2 : 0;
  return `${sign}${SYMBOLS[currency] ?? `${currency} `}${(n / 100).toLocaleString('en-NG', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export const two = (n: number) => String(n).padStart(2, '0');
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const and = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

/** A customer's first name, or "Mrs Okeke" whole. */
export const firstOf = (name: string) => (/^(Mrs|Mr|Ms|Dr|Miss|Alhaji|Chief)\b/.test(name) ? name : name.split(/\s+/)[0]);

/** A courier's first name; "A courier" with none. */
export const courierFirst = (c: Courier | null | undefined) => {
  const n = c?.name?.trim();
  return n ? n.split(/\s+/)[0] : c?.phone ? c.phone : 'A courier';
};

/** The phone's vehicles, in its order (vehicle_type.dart). */
export const VEHICLES: Record<string, string> = { motorcycle: 'Motorcycle', tricycle: 'Tricycle', car: 'Car', van: 'Van', bicycle: 'Bicycle' };
export const vehicleWords = (v?: string) => (v ? (VEHICLES[v] ?? v) : 'Vehicle not set');

export const courierName = (c: Courier | null | undefined) => c?.name?.trim() || c?.phone || 'A courier';

/** "12 Danube Street" as the line a person reads first. */
export const streetOf = (s: Stop) => (s.houseNumber && !s.street.startsWith(s.houseNumber) ? `${s.houseNumber} ${s.street}` : s.street);

/** "Danube Street": the street without its number. */
export const streetName = (s: Stop) => streetOf(s).replace(/^\d+\w?\s+/, '');

export const reasonWords = (r: Reason | null | undefined) => (r ? REASONS[r] : 'Not delivered');

const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Friday 30 October" for a day ("2026-10-30"); "Friday, October 30" where the month comes first; short: "Fri 30 Oct". */
export function dayWords(day: string, { weekday = true, short = false } = {}, s: DateStyle = style): string {
  const [y, m, d] = day.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const name = short ? WEEKDAY[wd].slice(0, 3) : WEEKDAY[wd];
  const month = short ? MONTH[m - 1].slice(0, 3) : MONTH[m - 1];
  const date = s.dayFirst ? `${d} ${month}` : `${month} ${d}`;
  return weekday ? (s.dayFirst ? `${name} ${date}` : `${name}, ${date}`) : date;
}

export const weekdayName = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
};

const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000);

/** A day up to today in words (PlanDays.label): "today", "yesterday", "Thursday" within the week, else "Thursday 22 October". */
export function dayBefore(day: string, today: string): string {
  const n = daysBetween(day, today);
  if (n <= 0) return 'today';
  if (n === 1) return 'yesterday';
  if (n < 7) return weekdayName(day);
  return dayWords(day);
}

/** When something last happened, with its day if not today (when_text.dart `dayTime`): "7:12 PM", "7:12 PM yesterday", "Wed 28 Oct". */
export function dayTime(t: number, now: number, zone: string): string {
  const w = wall(t, zone);
  const day = `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
  const n = wall(now, zone);
  const today = `${n.year}-${String(n.month).padStart(2, '0')}-${String(n.day).padStart(2, '0')}`;
  const gap = daysBetween(day, today);
  if (gap <= 0) return time(t, zone);
  if (gap === 1) return `${time(t, zone)} yesterday`;
  return dayWords(day, { short: true });
}
export const monthName = (m: number) => MONTH[m - 1];

/** "Monday" for a day this week or next, else "Monday 9 November": when a customer asked to have it. */
export function askedFor(day: string | null | undefined, today: string): string {
  if (!day) return 'another day';
  const diff = (Date.parse(day) - Date.parse(today)) / 86400000;
  if (diff === 1) return 'tomorrow';
  if (diff > 1 && diff < 7) return weekdayName(day);
  return dayWords(day);
}

/** How distances are said (distances.dart `DistanceUnit`): kilometres, or miles with feet or yards. */
export type Unit = 'km' | 'milesFeet' | 'milesYards';
const METRES_PER_MILE = 1609.344;
let unit: Unit = 'km';

/** Where road signs are in miles (`DistanceUnit.forCountry`): the UK and its Crown Dependencies with yards, the US and its territories but Puerto Rico with feet; kilometres everywhere else. */
export const unitOf = (country: string | null | undefined): Unit =>
  ['GB', 'IM', 'JE', 'GG'].includes(country?.toUpperCase() ?? '') ? 'milesYards' : ['US', 'GU', 'VI', 'AS', 'MP'].includes(country?.toUpperCase() ?? '') ? 'milesFeet' : 'km';

/** The business's unit, from the country its owner confirmed (set as the model is built). */
export const useUnit = (u: Unit) => void (unit = u);

/** A route's length: "14.2 km", "8.8 mi". */
export function length(m: number, u: Unit = unit): string {
  return u === 'km' ? `${(m / 1000).toFixed(1)} km` : `${(m / METRES_PER_MILE).toFixed(1)} mi`;
}

/** How far: "950 m" or "1.2 km"; in miles "450 ft" or "150 yd" under a tenth of a mile, then "0.8 mi". */
export function distance(m: number, u: Unit = unit): string {
  if (u === 'km') return m < 1000 ? `${Math.round(m / 10) * 10} m` : length(m, u);
  if (m >= METRES_PER_MILE / 10) return length(m, u);
  return u === 'milesYards' ? `${Math.round(m / 0.9144 / 10) * 10} yd` : `${Math.round(m / 0.3048 / 30) * 30} ft`;
}

export const esc = (s: string) => s;

/** The country calling codes the web knows, for a customer's number typed without one (TrackingShare.whatsAppNumber). */
const CALLING: Record<string, string> = { NG: '234', GH: '233', KE: '254', ZA: '27', CI: '225', SN: '221', EG: '20', GB: '44', US: '1', AE: '971', TR: '90', RW: '250', UG: '256', TZ: '255', CM: '237', BJ: '229', TG: '228' };

/** The number wa.me wants: country code and digits, no +. Null when there's no code to put in front of it. */
export function whatsAppNumber(phone: string | undefined, country: string | null): string | null {
  if (!phone) return null;
  const raw = phone.trim();
  let digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) {
    // Already international.
  } else if (digits.startsWith('00')) digits = digits.slice(2);
  else {
    const code = country ? CALLING[country] : null;
    if (!code) return null;
    if (!digits.startsWith(code) || digits.length < 11) digits = code + digits.replace(/^0+/, '');
  }
  return digits.length >= 10 ? digits : null;
}

/**
 * Whether a search [q] is a phone number found in [phone], however either was written: "0803 123 4567", "08031234567"
 * and "+234 803 123 4567" are the same number in Nigeria. Only for 7 digits or more, so a house number doesn't match.
 */
export function phoneMatches(q: string, phone: string | null | undefined, country: string | null): boolean {
  if (!phone || q.replace(/\D/g, '').length < 7 || /[a-z]/i.test(q)) return false;
  const a = whatsAppNumber(q, country) ?? q.replace(/\D/g, '').replace(/^0+/, '');
  const b = whatsAppNumber(phone, country) ?? phone.replace(/\D/g, '').replace(/^0+/, '');
  return b.includes(a) || b.endsWith(q.replace(/\D/g, '').replace(/^0+/, ''));
}
